import axios from "axios";

let isRefreshing = false;
let refreshSubscribers = [];

const onRefreshed = (newToken) => {
  refreshSubscribers.forEach((cb) => cb(newToken));
  refreshSubscribers = [];
};

const addRefreshSubscriber = (callback) => {
  refreshSubscribers.push(callback);
};

const getStoredToken = () => {
  const direct = localStorage.getItem("auth_token");
  if (direct) return direct;

  try {
    const raw = localStorage.getItem("admin");
    if (raw) {
      const outer = JSON.parse(raw);
      const authStr = outer?.auth;
      if (authStr) {
        const auth =
          typeof authStr === "string" ? JSON.parse(authStr) : authStr;
        const token = auth?.session?.token;
        if (token) return token;
      }
    }
  } catch {}

  return null;
};

const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL || "http://localhost:1338/api/",
  withCredentials: true,
  headers: {
    "ngrok-skip-browser-warning": "true",
    "Cache-Control": "no-cache",
  },
});

api.interceptors.request.use(
  (config) => {
    const token = getStoredToken();
    if (token) {
      config.headers.Authorization = token.startsWith("Bearer ")
        ? token
        : `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => response.data,

  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const errorMessage = error.response?.data?.error?.message || "";

    const isTokenExpired =
      status === 401 &&
      (errorMessage === "JWT_EXPIRED" ||
        errorMessage.toLowerCase().includes("expired"));

    const isAuthEndpoint =
      originalRequest.url?.includes("/auth/refresh") ||
      originalRequest.url?.includes("/auth/login") ||
      originalRequest.url?.includes("/auth/logout");

    if (isTokenExpired && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;
      console.log(
        "⚠️ [Chat Axios] Access Token Expired. Initiating Refresh Flow...",
      );

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          addRefreshSubscriber((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(api(originalRequest));
          });
        });
      }

      isRefreshing = true;

      try {
        const refreshResponse = await axios.post(
          `${api.defaults.baseURL}auth/refresh`,
          { refreshToken: localStorage.getItem("refresh_token") },
          { withCredentials: true },
        );

        const newAccessToken = refreshResponse.data?.accessToken;
        const newRefreshToken = refreshResponse.data?.refreshToken;

        if (!newAccessToken)
          throw new Error("No access token in refresh response");

        console.log("✅ [Chat Axios] Successfully refreshed Access Token");

        localStorage.setItem("auth_token", newAccessToken);
        if (newRefreshToken) {
          console.log("✅ [Chat Axios] Successfully rotated Refresh Token");
          localStorage.setItem("refresh_token", newRefreshToken);
        }

        onRefreshed(newAccessToken);
        isRefreshing = false;

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        console.log(
          "❌ [Chat Axios] Refresh Failed. Redirecting to login.",
          refreshError?.message,
        );
        isRefreshing = false;
        refreshSubscribers = [];

        const currentToken = localStorage.getItem("auth_token");
        if (
          currentToken &&
          currentToken !==
            originalRequest.headers?.Authorization?.replace("Bearer ", "")
        ) {
          originalRequest.headers.Authorization = `Bearer ${currentToken}`;
          return api(originalRequest);
        }

        localStorage.removeItem("auth_token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("chat_user");

        // Reload to the app root — with no token, the app renders the in-app
        // SignInPage instead of bouncing out to the portal signin URL.
        window.location.href = "/";

        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

export default api;
